import Icon from '../../Icon/Icon';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { arrayToList, isNowPMAbsolute } from '../../../utils/functions';
import { getStrategyTranslationKey } from '../../../utils/strategy';
import CustomTableDragSelect from '../../Campaign/WizardBasics/Timing/CustomTableDragSelect/CustomTableDragSelect';

const CampaignBrief = (props) => {
    const { t } = useTranslation();
    const { timezone } = props;
    const buyingTime = isNowPMAbsolute() ? 'pm' : 'am';
    const campaign = useSelector((state) => state.dashboard.campaign);
    const pacingTranslationKey = getStrategyTranslationKey({
        strategy: campaign?.scheduler_mode,
    });
    const startCampaign = moment(new Date(campaign.start_period)).format(
        'DD/MM'
    );
    const endCampaign = moment(new Date(campaign.end_period)).format('DD/MM');

    const [showScheduleModal, setShowScheduleModal] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') setShowScheduleModal(false);
        };
        if (showScheduleModal) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showScheduleModal]);

    const formatDays = (days) => {
        const weekdays = [
            t('sections.campaigns.new-campaign.wizard-basics.timing.sunday'),
            t('sections.campaigns.new-campaign.wizard-basics.timing.monday'),
            t('sections.campaigns.new-campaign.wizard-basics.timing.tuesday'),
            t('sections.campaigns.new-campaign.wizard-basics.timing.wednesday'),
            t('sections.campaigns.new-campaign.wizard-basics.timing.thursday'),
            t('sections.campaigns.new-campaign.wizard-basics.timing.friday'),
            t('sections.campaigns.new-campaign.wizard-basics.timing.saturday'),
        ];
        const weekdayType = [
            t('sections.campaigns.new-campaign.wizard-basics.timing.every-day'),
            t(
                'sections.campaigns.new-campaign.wizard-basics.timing.bussiness-days'
            ),
            t('sections.campaigns.new-campaign.wizard-basics.timing.weekend'),
        ];
        const isWeekend =
            days.includes(0) && days.includes(6) && days.length === 2;
        const isBusinessDays =
            !isWeekend &&
            !(days.includes(0) || days.includes(6)) &&
            days.length === 5;
        const readableDays = days.map((day) => weekdays[day]);
        let result = weekdayType[isWeekend * 2 + isBusinessDays * 1];
        if (days.length < 7 && result === weekdayType[0]) {
            result = readableDays.join(', ');
        }
        return result;
    };

    const formatGenders = (genders) => {
        if (!genders) return '';
        const translatedGenders = genders.map((genderLetter) =>
            t(
                `sections.campaigns.new-campaign.wizard-audiences.demographics.gender.${genderLetter}`
            )
        );
        return arrayToList(translatedGenders);
    };

    const formatAge = (ages) => {
        if (!ages) return '';
        const agesScopesi =
            ages.length > 0
                ? `${campaign.scopesi_ages[0].split('-')[0]} ${t(
                      'common.words.to'
                  )} ${
                      campaign.scopesi_ages[
                          campaign.scopesi_ages.length - 1
                      ].split('-')[1]
                  }`
                : '';
        return agesScopesi;
    };

    const formatIncome = (incomes) => {
        if (!incomes) return '';
        const translatedIncomeLevels = incomes.map((letter) =>
            t(
                `sections.campaigns.new-campaign.wizard-audiences.demographics.income.${letter}`
            )
        );
        return arrayToList(translatedIncomeLevels);
    };

    /**
     * "If the current hour is in the array of hours enabled, return true, otherwise return false."
     *
     * @returns A boolean value
     */
    let isCampaignPlaying = () => {
        let today = new Date();
        let currentHour = today.getHours();
        let currentDay = today.getDay();
        const weeklyIndex = currentDay * 24 + currentHour;
        let bool = effectiveWeeklyHours.includes(weeklyIndex);

        return bool;
    };

    const formatAccuracy = (coverage) => {
        if (!coverage) return '';
        let accuracy = '';
        switch (coverage) {
            case '2.00':
                accuracy = 'high';
                break;
            case '1.00':
                accuracy = 'mid';
                break;
            case '0.00':
                accuracy = 'low';
                break;
            default:
                break;
        }

        return t(
            `sections.campaigns.new-campaign.wizard-audiences.demographics.accuracy.${accuracy}`
        );
    };

    const providerSelected = (idProvider) => {
        switch (idProvider) {
            case '1':
                return 'Scopesi';
            case '2':
                return 'Cinnecta';
            case '3':
                return 'Onemata';
            default:
                return '';
        }
    };

    return (
        <div className="block px-2 w-full">
            <div className="font-light text-xs uppercase">
                {t('sections.dashboard-individual.campaign-brief')}
            </div>
            <div className="flex flex-nowrap md:flex-wrap lg:flex-nowrap justify-between divide-dotted divide-x-2 lg:space-x-4">
                <ul className="w-1/4 md:w-3/6 lg:w-1/4 text-gray-400 text-xs font-light space-y-1">
                    <li className="mt-4 text-xs uppercase font-medium text-black">
                        <Icon
                            name="user"
                            fixedWidth
                            className="text-blue-300"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-tab.audiences'
                        )}
                    </li>
                    {[
                        {
                            property: t(
                                'sections.campaigns.new-campaign.wizard-audiences.demographics.provider.provider-title'
                            ),
                            value: providerSelected(campaign.provider_audience),
                        },
                        {
                            property: t(
                                'sections.campaigns.new-campaign.wizard-audiences.demographics.gender.gender-title'
                            ),
                            value: formatGenders(campaign.scopesi_gender),
                        },
                        {
                            property: t(
                                'sections.campaigns.new-campaign.wizard-audiences.demographics.age.age-title'
                            ),
                            value: formatAge(campaign.scopesi_ages),
                        },
                        {
                            property: t(
                                'sections.campaigns.new-campaign.wizard-audiences.demographics.income.income-title'
                            ),
                            value: formatIncome(campaign.scopesi_nse),
                        },
                        {
                            property: t(
                                'sections.campaigns.new-campaign.wizard-audiences.demographics.accuracy.accuracy-title'
                            ),
                            value: formatAccuracy(campaign.scopesi_coverage),
                        },
                    ].map((audience, index) => (
                        <li key={index}>
                            <span
                                className={`font-semibold ${
                                    audience.value.length === 0 && 'hidden'
                                }`}>
                                {audience.property}:
                            </span>{' '}
                            {audience.value}
                        </li>
                    ))}
                </ul>
                <ul className="w-1/4 md:w-3/6 lg:w-1/4 text-gray-400 text-xs font-light space-y-1">
                    <li className="mt-4 text-xs uppercase font-medium text-black">
                        <Icon
                            name="shipping-fast"
                            fixedWidth
                            className="text-blue-300"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.pacing'
                        )}
                    </li>
                    <li>
                        <span>{t(pacingTranslationKey)}</span>{' '}
                    </li>
                </ul>
                {/* TO DO
                <ul className="mx-4 text-gray-400 text-xs font-light space-y-1">
                    <li className="mt-4 text-xs uppercase font-medium text-black"><Icon name="sliders-v" fixedWidth className="text-blue-300" /> {t('sections.campaigns.new-campaign.wizard-basics.buyingRules.title')}</li>
                    {[].map((rule, index) =>
                        <li key={index}><span className="font-semibold">{rule.context}:</span> {rule.case}</li>
                    )}
                </ul> */}
                <ul className="w-1/4 md:w-3/6 lg:w-1/4 text-gray-400 text-xs font-light space-y-1">
                    <li className="mt-4 text-xs uppercase font-medium text-black">
                        <Icon
                            name="calendar-check"
                            fixedWidth
                            className="text-blue-300"
                        />{' '}
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.title'
                        )}
                    </li>
                    <li>
                        <span className="font-semibold">
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.timing.timezone'
                            )}
                            :
                        </span>{' '}
                        {timezone !== '' && `GMT${timezone.gmt}`}
                    </li>
                    <li>
                        <span className="font-semibold whitespace-nowrap">
                            {t(
                                'sections.campaigns.new-campaign.wizard-review.blocks.basics.period'
                            )}
                            :
                        </span>
                        <br /> {startCampaign} - {endCampaign}{' '}
                    </li>
                </ul>
                <ul className="w-1/4 md:w-3/6 lg:w-1/4 text-gray-400 text-xs font-light space-y-1">
                    <li className="mt-4 text-xs uppercase font-medium text-black">
                        {t(
                            'sections.campaigns.new-campaign.wizard-basics.timing.hours'
                        )}
                    </li>
                    <li>
                        <div>
                            <button
                                type="button"
                                onClick={() => setShowScheduleModal(true)}
                                className="inline-flex items-center px-3 py-1 rounded-full border bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium shadow-sm hover:shadow"
                                aria-haspopup="dialog"
                                aria-expanded={showScheduleModal}>
                                <span className="text-xs">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-basics.timing.day-parting'
                                    )}{' '}
                                </span>
                            </button>
                        </div>
                    </li>
                </ul>
            </div>
            {showScheduleModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Cronograma Schedule">
                    {/* backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowScheduleModal(false)}
                    />

                    {/* modal box */}
                    <div
                        className="
                                relative bg-white rounded-lg shadow-2xl
                                p-3 z-10
                                max-w-fit
                                max-h-[80vh]
                            "
                        onClick={(e) => e.stopPropagation()}>
                        {/* header */}
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-400">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-basics.timing.day-parting'
                                )}{' '}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowScheduleModal(false)}
                                className="p-1 rounded hover:bg-gray-100">
                                <Icon
                                    name="times"
                                    fixedWidth
                                    className="text-gray-500 h-5 hover:text-black"
                                />
                            </button>
                        </div>

                        {/* content */}
                        <div className="overflow-auto max-h-[70vh] flex justify-center">
                            <div className="origin-top-left">
                                <CustomTableDragSelect
                                    readOnly={true}
                                    change={() => {}}
                                    campaign={campaign}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CampaignBrief;
